import { KDF_PARAMS, AUTH_HASH_PARAMS, HKDF_PARAMS } from './constants';

// PBKDF2 iteration count for master key derivation (Argon2id params don't apply to PBKDF2)
const PBKDF2_ITERATIONS = 600_000;

// Helper to cast Uint8Array for TS5 ArrayBuffer compatibility
const buf = (u: Uint8Array): ArrayBuffer =>
  u.slice().buffer as ArrayBuffer;

/**
 * Derive a 32-byte master key from a password and salt using PBKDF2-SHA256.
 *
 * Uses the Web Crypto API for maximum browser compatibility.
 * The master key is used client-side only for vault encryption.
 */
export async function deriveMasterKey(
  password: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const passwordBytes = encoder.encode(password);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    buf(passwordBytes),
    { name: 'PBKDF2' },
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: buf(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    KDF_PARAMS.keyBytes * 8,
  );

  return new Uint8Array(derivedBits);
}

/**
 * Derive a 32-byte authentication hash from a password and email using
 * PBKDF2-SHA256 via WebCrypto.
 */
export async function deriveAuthHash(
  password: string,
  email: string,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const passwordBytes = encoder.encode(password);
  const saltBytes = encoder.encode(email);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    buf(passwordBytes),
    { name: 'PBKDF2' },
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: buf(saltBytes),
      iterations: AUTH_HASH_PARAMS.iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    AUTH_HASH_PARAMS.keyBytes * 8,
  );

  return new Uint8Array(derivedBits);
}

/**
 * Derive a Key Encryption Key (KEK) from a master key using HKDF-SHA256.
 */
export async function deriveKEK(
  masterKey: Uint8Array,
  purpose: string,
): Promise<{ key: Uint8Array; salt: Uint8Array }> {
  const salt = new Uint8Array(HKDF_PARAMS.saltBytes);
  crypto.getRandomValues(salt);

  const info = new TextEncoder().encode(
    `${HKDF_PARAMS.infoPrefix}:${purpose}`,
  );

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    buf(masterKey),
    { name: 'HKDF' },
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: HKDF_PARAMS.algorithm,
      salt: buf(salt),
      info: buf(info),
    },
    keyMaterial,
    KDF_PARAMS.keyBytes * 8,
  );

  return { key: new Uint8Array(derivedBits), salt };
}

/**
 * Derive a Key Encryption Key with a known salt (for re-derivation).
 */
export async function deriveKEKWithSalt(
  masterKey: Uint8Array,
  purpose: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const info = new TextEncoder().encode(
    `${HKDF_PARAMS.infoPrefix}:${purpose}`,
  );

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    buf(masterKey),
    { name: 'HKDF' },
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: HKDF_PARAMS.algorithm,
      salt: buf(salt),
      info: buf(info),
    },
    keyMaterial,
    KDF_PARAMS.keyBytes * 8,
  );

  return new Uint8Array(derivedBits);
}
